// src/pages/ServiceWarrantyConfig.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Trash2, Edit, Package, User, Hash, X, Calendar, Shield, Building2, Download, FileText } from "lucide-react";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * ServiceWarrantyConfig.jsx
 * - Combined Service & Warranty Configuration + Asset Allocation
 * - Fonts: Poppins + Roboto (inline import)
 * - Features:
 *    • CRUD Service / Warranty templates
 *    • Asset allocation with warranty tracking
 *    • Fields: Item, SR Number, Expiry Date, Warranty Years, Company
 *    • LocalStorage persistence
 *    • Import / Export JSON
 *    • Employee assignment tracking
 */

/* ------------------ constants ------------------ */
const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');
`;

const LS_KEY = "svc_warranty_cfg_v1";
const ASSETS_LS_KEY = "asset_allocation_v1";
const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random()*99999)}`;

const DEFAULT_DATA = [
  {
    id: uid("svc"),
    name: "Laptop Standard Warranty",
    type: "Warranty", // Warranty | Service | SLA
    durationMonths: 12,
    coverage: "Hardware defects, manufacturing faults. On-site repair within 3 business days.",
    priceINR: 0, // warranty may be free
    sla: { responseHours: 48, resolutionHours: 72 },
    active: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: uid("svc"),
    name: "Premium On-site Support",
    type: "Service",
    durationMonths: 24,
    coverage: "On-site next-business-day, annual preventive maintenance, priority phone support.",
    priceINR: 14999,
    sla: { responseHours: 4, resolutionHours: 24 },
    active: true,
    createdAt: new Date().toISOString(),
  },
];

const ASSET_CATEGORIES = [
  "Mobile", "Laptop", "Server", "Network Device", "Peripheral", "Other Device"
];

const ASSET_STATUS_OPTIONS = [
  "Available", "Assigned", "In Use", "Returned", "Under Maintenance", 
  "Damaged", "Lost / Stolen", "Retired", "Disposed", "Requested", 
  "Approved", "Rejected", "Reallocated", "Under Warranty", 
  "Warranty Expired", "AMC Active", "AMC Expired", "Other"
];

const DEFAULT_ASSETS = [
  {
    id: uid("asset"),
    assetId: "AST-001",
    serialNumber: "DL001234",
    brandName: "Dell",
    itemName: "Dell Laptop Inspiron 15",
    category: "Laptop",
    assignedTo: "John Doe (EMP001)",
    status: "In Use",
    purchaseDate: "2023-01-15",
    allocationDate: "2023-01-20",
    warrantyExpiryDate: "2025-01-15",
    warrantyYears: 2,
    deployedTo: "IT Department",
    company: "Dell Technologies",
    assignedEmpId: "EMP001",
    assignedEmpName: "John Doe",
    createdAt: new Date().toISOString(),
  },
  {
    id: uid("asset"),
    assetId: "AST-002",
    serialNumber: "HP567890",
    brandName: "HP",
    itemName: "HP Printer LaserJet Pro",
    category: "Peripheral",
    assignedTo: "Jane Smith (EMP002)",
    status: "Assigned",
    purchaseDate: "2023-08-15",
    allocationDate: "2023-08-20",
    warrantyExpiryDate: "2024-08-15",
    warrantyYears: 1,
    deployedTo: "HR Department",
    company: "HP Inc.",
    assignedEmpId: "EMP002",
    assignedEmpName: "Jane Smith",
    createdAt: new Date().toISOString(),
  },
];

/* ------------------ Modal (portal, compact) ------------------ */
function Modal({ open, title, width = "max-w-3xl", onClose, children }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(10,11,13,0.35)" }}
      >
        <motion.div
          initial={{ y: -6, scale: 0.995 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: -6, scale: 0.995 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[78vh] overflow-auto`}
        >
          <div className="sticky top-0 bg-white z-10 border-b px-5 py-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <div>
              <button onClick={onClose} className="px-3 py-1 rounded hover:bg-slate-100">✕</button>
            </div>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

/* ------------------ Helpers ------------------ */
const formatINR = (n = 0) => {
  try { return "₹ " + Number(n).toLocaleString("en-IN"); }
  catch { return `₹ ${n}`; }
};

/* ------------------ Page ------------------ */
export default function ServiceWarrantyConfig() {
  const [activeTab, setActiveTab] = useState("assets"); // assets | reports | activity
  const [reportDateRange, setReportDateRange] = useState("all"); // all | daily | weekly | monthly | quarterly | half-yearly | yearly | custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  
  // Service & Warranty state
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_DATA;
    } catch {
      return DEFAULT_DATA;
    }
  });

  // Asset allocation state
  const [assets, setAssets] = useState(() => {
    try {
      const raw = localStorage.getItem(ASSETS_LS_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_ASSETS;
    } catch {
      return DEFAULT_ASSETS;
    }
  });

  const [customCategories, setCustomCategories] = useState(() => {
    try {
      const raw = localStorage.getItem('custom_categories_v1');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [customStatuses, setCustomStatuses] = useState(() => {
    try {
      const raw = localStorage.getItem('custom_statuses_v1');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newStatusName, setNewStatusName] = useState('');

  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [sortBy, setSortBy] = useState("recent"); // recent | price | duration
  const [editorOpen, setEditorOpen] = useState(false);
  const [assetEditorOpen, setAssetEditorOpen] = useState(false);
  const [viewAssetOpen, setViewAssetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    try {
      localStorage.setItem(ASSETS_LS_KEY, JSON.stringify(assets));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Removing PDF data from storage.');
        const assetsWithoutPdf = assets.map(a => ({
          ...a,
          invoicePdf: undefined,
          invoicePdfType: undefined
        }));
        localStorage.setItem(ASSETS_LS_KEY, JSON.stringify(assetsWithoutPdf));
      }
    }
  }, [assets]);

  useEffect(() => {
    localStorage.setItem('custom_categories_v1', JSON.stringify(customCategories));
  }, [customCategories]);

  useEffect(() => {
    localStorage.setItem('custom_statuses_v1', JSON.stringify(customStatuses));
  }, [customStatuses]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1600);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (s) => {
    setToast(s);
  };

  const addCustomCategory = () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }
    if (ASSET_CATEGORIES.includes(newCategoryName) || customCategories.includes(newCategoryName)) {
      alert('Category already exists');
      return;
    }
    setCustomCategories(prev => [...prev, newCategoryName.trim()]);
    setNewCategoryName('');
    setShowCategoryForm(false);
    showToast('Category added successfully');
  };

  const addCustomStatus = () => {
    if (!newStatusName.trim()) {
      alert('Please enter a status name');
      return;
    }
    if (ASSET_STATUS_OPTIONS.includes(newStatusName) || customStatuses.includes(newStatusName)) {
      alert('Status already exists');
      return;
    }
    setCustomStatuses(prev => [...prev, newStatusName.trim()]);
    setNewStatusName('');
    setShowStatusForm(false);
    showToast('Status added successfully');
  };

  const allCategories = [...ASSET_CATEGORIES, ...customCategories];
  const allStatuses = [...ASSET_STATUS_OPTIONS, ...customStatuses];

  /* CRUD */
  const openNew = () => {
    setEditing({
      id: uid("svc"),
      name: "",
      type: "Warranty",
      durationMonths: 12,
      coverage: "",
      priceINR: 0,
      sla: { responseHours: 48, resolutionHours: 72 },
      active: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (it) => {
    setEditing(JSON.parse(JSON.stringify(it)));
    setEditorOpen(true);
  };

  const saveItem = (payload) => {
    // validation
    if (!payload.name || payload.name.trim().length < 2) {
      alert("Please supply a valid name (2+ chars)."); return;
    }
    if (payload.durationMonths <= 0) { alert("Duration must be > 0"); return; }
    // save
    setItems(prev => {
      const idx = prev.findIndex(p => p.id === payload.id);
      if (idx === -1) return [ { ...payload, createdAt: new Date().toISOString() }, ...prev ];
      const cp = [...prev]; cp[idx] = { ...payload, updatedAt: new Date().toISOString() }; return cp;
    });
    setEditorOpen(false);
    showToast("Saved");
  };

  const toggleActive = (id) => {
    setItems(prev => prev.map(i => i.id === id ? ({ ...i, active: !i.active }) : i));
    showToast("Updated");
  };

  const removeItem = (id) => {
    if (!window.confirm("Delete configuration? This cannot be undone.")) return;
    setItems(prev => prev.filter(i => i.id !== id));
    showToast("Deleted");
  };

  /* Asset CRUD */
  const generateNextAssetId = () => {
    if (assets.length === 0) return "AST-001";
    
    // Extract numeric parts from existing asset IDs and find the highest
    const existingNumbers = assets
      .map(asset => {
        const match = asset.assetId?.match(/AST-(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(num => !isNaN(num));
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    const nextNumber = maxNumber + 1;
    
    return `AST-${nextNumber.toString().padStart(3, '0')}`;
  };

  const openNewAsset = () => {
    setEditingAsset({
      id: uid("asset"),
      assetId: generateNextAssetId(),
      serialNumber: "",
      brandName: "",
      itemName: "",
      category: "Laptop",
      assignedTo: "",
      status: "Available",
      purchaseDate: "",
      allocationDate: "",
      warrantyExpiryDate: "",
      warrantyYears: 1,
      deployedTo: "",
      company: "",
      assignedEmpId: "",
      assignedEmpName: "",
      siteLocation: "",
      invoicePdf: "",
      invoicePdfName: "",
      invoicePdfType: "",
    });
    setAssetEditorOpen(true);
  };

  const openViewAsset = (asset) => {
    setViewingAsset(asset);
    setViewAssetOpen(true);
  };

  const openEditAsset = (asset) => {
    setEditingAsset(JSON.parse(JSON.stringify(asset)));
    setAssetEditorOpen(true);
  };

  const saveAsset = (payload) => {
    if (!payload.itemName || payload.itemName.trim().length < 2) {
      alert("Please supply a valid item name (2+ chars)."); return;
    }
    if (!payload.assetId) {
      alert("Asset ID is required"); return;
    }
    if (!payload.serialNumber) {
      alert("Serial number is required"); return;
    }
    // Update assignedTo field based on employee details
    if (payload.assignedEmpName && payload.assignedEmpId) {
      payload.assignedTo = `${payload.assignedEmpName} (${payload.assignedEmpId})`;
    } else {
      payload.assignedTo = "";
    }
    setAssets(prev => {
      const idx = prev.findIndex(p => p.id === payload.id);
      if (idx === -1) return [ { ...payload, createdAt: new Date().toISOString() }, ...prev ];
      const cp = [...prev]; cp[idx] = { ...payload, updatedAt: new Date().toISOString() }; return cp;
    });
    setAssetEditorOpen(false);
    showToast("Asset Saved");
  };

  const removeAsset = (id) => {
    if (!window.confirm("Delete asset? This cannot be undone.")) return;
    setAssets(prev => prev.filter(i => i.id !== id));
    showToast("Asset Deleted");
  };

  /* Import / Export */
  const exportAll = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `service_warranty_config_${new Date().toISOString().slice(0,10)}.json`;
    a.click();
  };

  const importFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        const normalized = arr.map(x => ({ ...x, id: x.id || uid("svc") }));
        setItems(prev => ([ ...normalized, ...prev ]));
        showToast("Imported");
      } catch {
        alert("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  /* derived list */
  const filtered = useMemo(() => {
    let result = items.slice();
    if (query) {
      const q = query.toLowerCase().trim();
      result = result.filter(i => i.name.toLowerCase().includes(q) || (i.coverage||"").toLowerCase().includes(q));
    }
    if (filterType !== "All") result = result.filter(i => i.type === filterType);
    if (sortBy === "recent") result.sort((a,b)=> new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0));
    if (sortBy === "price") result.sort((a,b)=> (b.priceINR||0) - (a.priceINR||0));
    if (sortBy === "duration") result.sort((a,b)=> (b.durationMonths||0) - (a.durationMonths||0));
    return result;
  }, [items, query, filterType, sortBy]);

  const filteredAssets = useMemo(() => {
    let result = assets.slice();
    if (query) {
      const q = query.toLowerCase().trim();
      result = result.filter(a => 
        a.itemName.toLowerCase().includes(q) || 
        (a.serialNumber||"").toLowerCase().includes(q) ||
        (a.assetId||"").toLowerCase().includes(q) ||
        (a.brandName||"").toLowerCase().includes(q) ||
        a.company.toLowerCase().includes(q) ||
        (a.assignedEmpName||"").toLowerCase().includes(q) ||
        (a.status||"").toLowerCase().includes(q)
      );
    }
    // Sort by Asset ID for sequential display
    result.sort((a, b) => {
      const aNum = parseInt(a.assetId?.replace('AST-', '') || '0');
      const bNum = parseInt(b.assetId?.replace('AST-', '') || '0');
      return aNum - bNum;
    });
    return result;
  }, [assets, query]);

  // Filter assets by date range
  const dateFilteredAssets = useMemo(() => {
    if (reportDateRange === "all") return filteredAssets;
    
    const now = new Date();
    let startDate = new Date();
    
    switch (reportDateRange) {
      case "daily":
        startDate.setDate(now.getDate() - 1);
        break;
      case "weekly":
        startDate.setDate(now.getDate() - 7);
        break;
      case "monthly":
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "quarterly":
        startDate.setMonth(now.getMonth() - 3);
        break;
      case "half-yearly":
        startDate.setMonth(now.getMonth() - 6);
        break;
      case "yearly":
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case "custom":
        if (!customStartDate || !customEndDate) return filteredAssets;
        startDate = new Date(customStartDate);
        const endDate = new Date(customEndDate);
        return filteredAssets.filter(asset => {
          const assetDate = new Date(asset.allocationDate || asset.purchaseDate || asset.createdAt);
          return assetDate >= startDate && assetDate <= endDate;
        });
      default:
        return filteredAssets;
    }
    
    return filteredAssets.filter(asset => {
      const assetDate = new Date(asset.allocationDate || asset.purchaseDate || asset.createdAt);
      return assetDate >= startDate;
    });
  }, [filteredAssets, reportDateRange, customStartDate, customEndDate]);
  // Export functions
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Asset Report', 14, 22);
    
    // Sort assets by Asset ID for sequential order
    const sortedAssets = [...dateFilteredAssets].sort((a, b) => {
      const aNum = parseInt(a.assetId?.replace('AST-', '') || '0');
      const bNum = parseInt(b.assetId?.replace('AST-', '') || '0');
      return aNum - bNum;
    });
    
    const tableData = sortedAssets.map(asset => [
      asset.assetId || '',
      asset.serialNumber || '',
      asset.brandName || '',
      asset.category || '',
      asset.itemName || '',
      asset.assignedTo || '',
      asset.status || '',
      asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : '',
      asset.allocationDate ? new Date(asset.allocationDate).toLocaleDateString() : '',
      asset.warrantyExpiryDate ? new Date(asset.warrantyExpiryDate).toLocaleDateString() : '',
      asset.warrantyYears || '',
      asset.deployedTo || ''
    ]);

    autoTable(doc, {
      head: [['Asset ID', 'Serial No.', 'Brand', 'Category', 'Name', 'Assigned To', 'Status', 'Purchase Date', 'Allocation Date', 'Warranty Expiry', 'Warranty Years', 'Deployed To']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }
    });

    doc.save(`asset-report-${new Date().toISOString().slice(0,10)}.pdf`);
    showToast('PDF exported successfully');
  };

  const exportToExcel = () => {
    // Sort assets by Asset ID for sequential order
    const sortedAssets = [...dateFilteredAssets].sort((a, b) => {
      const aNum = parseInt(a.assetId?.replace('AST-', '') || '0');
      const bNum = parseInt(b.assetId?.replace('AST-', '') || '0');
      return aNum - bNum;
    });
    
    const excelData = sortedAssets.map(asset => ({
      'Asset ID': asset.assetId || '',
      'Serial Number': asset.serialNumber || '',
      'Brand Name': asset.brandName || '',
      'Category': asset.category || '',
      'Asset Name': asset.itemName || '',
      'Assigned To': asset.assignedTo || '',
      'Status': asset.status || '',
      'Purchase Date': asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : '',
      'Allocation Date': asset.allocationDate ? new Date(asset.allocationDate).toLocaleDateString() : '',
      'Warranty Expiry Date': asset.warrantyExpiryDate ? new Date(asset.warrantyExpiryDate).toLocaleDateString() : '',
      'Number of Warranty Years': asset.warrantyYears || '',
      'Deployed To': asset.deployedTo || '',
      'Company': asset.company || ''
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assets');
    XLSX.writeFile(wb, `asset-report-${new Date().toISOString().slice(0,10)}.xlsx`);
    showToast('Excel exported successfully');
  };

  /* small calculator for example pricing (optional UI) */
  const suggestPrice = (type, duration) => {
    if (type === "Warranty") return 0;
    if (type === "Service") {
      // naive suggestion (INR): base * months
      const base = 699; // per month baseline
      return Math.round(base * duration);
    }
    return 0;
  };

  /* UI pieces */
  function ItemRow({ it }) {
    return (
      <div className="bg-white border rounded-lg p-4 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="flex flex-col">
              <div className={`px-2 py-1 rounded text-xs font-semibold ${it.type === "Warranty" ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>
                {it.type}
              </div>
            </div>
            <div>
              <div className="text-lg font-semibold text-slate-900">{it.name}</div>
              <div className="text-sm text-slate-500 mt-1 max-w-xl">{it.coverage || <i className="text-slate-400">No coverage description</i>}</div>
              <div className="text-xs text-slate-500 mt-2">
                Duration: <span className="font-medium">{it.durationMonths} months</span> · SLA: {it.sla?.responseHours}h / {it.sla?.resolutionHours}h
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-3">
          <div className="text-right">
            <div className="text-sm text-slate-500">Price</div>
            <div className="text-lg font-semibold">{formatINR(it.priceINR)}</div>
            <div className="text-xs text-slate-400 mt-1">Created: {new Date(it.createdAt).toLocaleDateString()}</div>
          </div>

          <div className="flex flex-col gap-2">
            <button onClick={()=>openEdit(it)} className="px-3 py-1 bg-white border rounded text-sm hover:bg-slate-50">Edit</button>
            <button onClick={()=>toggleActive(it.id)} className={`px-3 py-1 rounded text-sm ${it.active ? "bg-emerald-50 text-emerald-700 border" : "bg-rose-50 text-rose-700 border"}`}>
              {it.active ? "Active" : "Inactive"}
            </button>
            <button onClick={()=>removeItem(it.id)} className="px-3 py-1 rounded text-sm bg-rose-50 text-rose-700 border">Delete</button>
          </div>
        </div>
      </div>
    );
  }

  function AssetRow({ asset }) {
    const isExpired = new Date(asset.expiryDate) < new Date();
    return (
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Package className="text-teal-600 mt-1" size={20} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900">{asset.itemName}</h3>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  isExpired ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                }`}>
                  {isExpired ? "Expired" : "Active"}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                <div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Hash size={14} />
                    <span>SR Number</span>
                  </div>
                  <div className="font-medium">{asset.srNumber}</div>
                </div>
                
                <div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Calendar size={14} />
                    <span>Expiry Date</span>
                  </div>
                  <div className="font-medium">{new Date(asset.expiryDate).toLocaleDateString()}</div>
                </div>
                
                <div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Shield size={14} />
                    <span>Warranty</span>
                  </div>
                  <div className="font-medium">{asset.warrantyYears} years</div>
                </div>
                
                <div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Building2 size={14} />
                    <span>Company</span>
                  </div>
                  <div className="font-medium">{asset.company}</div>
                </div>
              </div>
              
              {asset.assignedEmpName && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <User size={14} className="text-slate-500" />
                  <span className="text-slate-500">Assigned to:</span>
                  <span className="font-medium">{asset.assignedEmpName} ({asset.assignedEmpId})</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <button onClick={() => openEditAsset(asset)} className="px-3 py-1 bg-white border rounded text-sm hover:bg-slate-50 flex items-center gap-1">
              <Edit size={14} /> Edit
            </button>
            <button onClick={() => removeAsset(asset.id)} className="px-3 py-1 rounded text-sm bg-rose-50 text-rose-700 border hover:bg-rose-100 flex items-center gap-1">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* Editor form */
  function Editor({ model, onSave, onCancel }) {
    const [form, setForm] = useState(model);

    useEffect(()=> setForm(model), [model]);

    if (!form) return null;

    const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600">Asset Name</label>
            <input value={form.name} onChange={(e)=> update({ name: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Type</label>
            <select value={form.type} onChange={(e)=> update({ type: e.target.value })} className="w-full px-3 py-2 border rounded">
              <option>Warranty</option>
              <option>Service</option>
              <option>SLA</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-slate-600">Duration (months)</label>
            <input type="number" min={1} value={form.durationMonths} onChange={(e)=> update({ durationMonths: Number(e.target.value||0) })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Price (INR)</label>
            <input type="number" min={0} value={form.priceINR} onChange={(e)=> update({ priceINR: Number(e.target.value||0) })} className="w-full px-3 py-2 border rounded" />
            <div className="text-xs text-slate-400 mt-1">Suggested: {formatINR(suggestPrice(form.type, form.durationMonths))}</div>
          </div>
        </div>

        <div>
          <label className="block text-xs text-slate-600">Coverage / Description</label>
          <textarea value={form.coverage} onChange={(e)=> update({ coverage: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded"></textarea>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600">SLA - Response (hours)</label>
            <input type="number" min={0} value={form.sla?.responseHours || 0} onChange={(e)=> update({ sla: { ...(form.sla||{}), responseHours: Number(e.target.value||0) } })} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-xs text-slate-600">SLA - Resolution (hours)</label>
            <input type="number" min={0} value={form.sla?.resolutionHours || 0} onChange={(e)=> update({ sla: { ...(form.sla||{}), resolutionHours: Number(e.target.value||0) } })} className="w-full px-3 py-2 border rounded" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <button onClick={onCancel} className="px-4 py-2 bg-white border rounded">Cancel</button>
          <button onClick={()=> onSave(form)} className="px-4 py-2 bg-indigo-600 text-white rounded">Save Configuration</button>
        </div>
      </div>
    );
  }

  /* Asset Viewer component */
  function AssetViewer({ asset, onClose }) {
    if (!asset) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Basic Information</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Asset ID:</span> {asset.assetId || 'N/A'}</div>
              <div><span className="font-medium">Serial Number:</span> {asset.serialNumber || 'N/A'}</div>
              <div><span className="font-medium">Brand Name:</span> {asset.brandName || 'N/A'}</div>
              <div><span className="font-medium">Asset Name:</span> {asset.itemName || 'N/A'}</div>
              <div><span className="font-medium">Category:</span> {asset.category || 'N/A'}</div>
              <div><span className="font-medium">Company:</span> {asset.company || 'N/A'}</div>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Assignment Details</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Status:</span> 
                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                  ['Available', 'Assigned', 'In Use', 'Under Warranty', 'AMC Active'].includes(asset.status) ? 'bg-green-100 text-green-800' :
                  ['Returned', 'Approved', 'Reallocated'].includes(asset.status) ? 'bg-blue-100 text-blue-800' :
                  ['Under Maintenance', 'Requested'].includes(asset.status) ? 'bg-yellow-100 text-yellow-800' :
                  ['Damaged', 'Lost / Stolen', 'Retired', 'Disposed', 'Rejected', 'Warranty Expired', 'AMC Expired'].includes(asset.status) ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {asset.status}
                </span>
              </div>
              <div><span className="font-medium">Assigned To:</span> {asset.assignedTo || 'Unassigned'}</div>
              <div><span className="font-medium">Employee ID:</span> {asset.assignedEmpId || 'N/A'}</div>
              <div><span className="font-medium">Employee Name:</span> {asset.assignedEmpName || 'N/A'}</div>
              <div><span className="font-medium">Deployed To:</span> {asset.deployedTo || 'N/A'}</div>
              <div><span className="font-medium">Site Location:</span> {asset.siteLocation || 'N/A'}</div>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Dates & Warranty</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">Purchase Date:</span> {asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : 'N/A'}</div>
              <div><span className="font-medium">Allocation Date:</span> {asset.allocationDate ? new Date(asset.allocationDate).toLocaleDateString() : 'N/A'}</div>
              <div><span className="font-medium">Warranty Expiry:</span> {asset.warrantyExpiryDate ? new Date(asset.warrantyExpiryDate).toLocaleDateString() : 'N/A'}</div>
              <div><span className="font-medium">Warranty Years:</span> {asset.warrantyYears || 'N/A'}</div>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Invoice & Documents</h3>
            <div className="space-y-3">
              {asset.invoicePdfName ? (
                <div>
                  <span className="text-sm font-medium block mb-2">Invoice PDF:</span>
                  <div className="text-sm text-slate-600 flex items-center gap-2">
                    📄 {asset.invoicePdfName}
                    <button 
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = asset.invoicePdf;
                        link.download = asset.invoicePdfName;
                        link.click();
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs underline"
                    >
                      Download
                    </button>
                    <button 
                      onClick={() => {
                        if (asset.invoicePdf && asset.invoicePdf.startsWith('data:')) {
                          const blob = fetch(asset.invoicePdf).then(res => res.blob());
                          blob.then(b => {
                            const url = URL.createObjectURL(b);
                            window.open(url, '_blank');
                          });
                        } else {
                          alert('PDF not available');
                        }
                      }}
                      className="text-green-600 hover:text-green-800 text-xs underline"
                    >
                      View
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">No invoice PDF available</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 bg-white border rounded hover:bg-gray-50">Close</button>
        </div>
      </div>
    );
  }

  /* Asset Editor form */
  function AssetEditor({ model, onSave, onCancel }) {
    const [form, setForm] = useState(model);

    useEffect(()=> setForm(model), [model]);

    if (!form) return null;

    const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600">Asset ID *</label>
            <input value={form.assetId || ''} onChange={(e)=> update({ assetId: e.target.value })} className="w-full px-3 py-2 border rounded bg-gray-50" placeholder="e.g., AST-001" readOnly />
            <div className="text-xs text-slate-400 mt-1">Auto-generated sequential ID</div>
          </div>

          <div>
            <label className="block text-xs text-slate-600">Serial Number *</label>
            <input value={form.serialNumber || ''} onChange={(e)=> update({ serialNumber: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., DL001234" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Brand Name *</label>
            <input value={form.brandName || ''} onChange={(e)=> update({ brandName: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., Dell" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Asset Name *</label>
            <input value={form.itemName || ''} onChange={(e)=> update({ itemName: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., Dell Laptop Inspiron 15" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Category *</label>
            <div className="flex items-center gap-2">
              <select value={form.category || 'Laptop'} onChange={(e)=> update({ category: e.target.value })} className="flex-1 px-3 py-2 border rounded">
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {form.category === 'Other Device' && (
                <button
                  type="button"
                  onClick={() => setShowCategoryForm(true)}
                  className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center"
                  title="Add new category"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-600">Status *</label>
            <div className="flex items-center gap-2">
              <select value={form.status || 'Available'} onChange={(e)=> update({ status: e.target.value })} className="flex-1 px-3 py-2 border rounded">
                {allStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              {form.status === 'Other' && (
                <button
                  type="button"
                  onClick={() => setShowStatusForm(true)}
                  className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center"
                  title="Add new status"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-600">Purchase Date</label>
            <input type="date" value={form.purchaseDate || ''} onChange={(e)=> update({ purchaseDate: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Allocation Date</label>
            <input type="date" value={form.allocationDate || ''} onChange={(e)=> update({ allocationDate: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Warranty Expiry Date</label>
            <input type="date" value={form.warrantyExpiryDate || ''} onChange={(e)=> update({ warrantyExpiryDate: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Number of Warranty Years</label>
            <input type="number" min={0} max={10} value={form.warrantyYears || 1} onChange={(e)=> update({ warrantyYears: Number(e.target.value||1) })} className="w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Deployed To (Optional)</label>
            <input value={form.deployedTo || ''} onChange={(e)=> update({ deployedTo: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., IT Department" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Company</label>
            <input value={form.company || ''} onChange={(e)=> update({ company: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., Dell Technologies" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Assigned Employee ID</label>
            <input value={form.assignedEmpId || ''} onChange={(e)=> update({ assignedEmpId: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., EMP001" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Assigned Employee Name</label>
            <input value={form.assignedEmpName || ''} onChange={(e)=> update({ assignedEmpName: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., John Doe" />
          </div>

          <div>
            <label className="block text-xs text-slate-600">Site Location (Optional)</label>
            <input value={form.siteLocation || ''} onChange={(e)=> update({ siteLocation: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., Building A, Floor 3" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600">Upload Invoice PDF (Optional)</label>
            <input type="file" accept=".pdf" onChange={(e)=> {
              const file = e.target.files[0];
              if (file) {
                if (file.type !== 'application/pdf') {
                  alert('Please select a PDF file only');
                  return;
                }
                const reader = new FileReader();
                reader.onload = (event) => {
                  update({ 
                    invoicePdf: event.target.result,
                    invoicePdfName: file.name,
                    invoicePdfType: file.type
                  });
                };
                reader.readAsDataURL(file);
              }
            }} className="w-full px-3 py-2 border rounded" />
            {form.invoicePdfName && (
              <div className="mt-2 text-sm text-slate-600 flex items-center gap-2">
                📄 {form.invoicePdfName}
                <button 
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = form.invoicePdf;
                    link.download = form.invoicePdfName;
                    link.click();
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs underline"
                >
                  Download
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <button onClick={onCancel} className="px-4 py-2 bg-white border rounded">Cancel</button>
          <button onClick={()=> onSave(form)} className="px-4 py-2 bg-teal-600 text-white rounded">Save Asset</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-slate-50" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <style>{FONT_IMPORT}</style>

      {/* toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} className="fixed right-6 top-6 bg-indigo-600 text-white px-4 py-2 rounded shadow">
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Inventory Management</h1>
            <p className="text-sm text-slate-500 mt-1">Define warranty offerings, service contracts, SLA templates and manage asset allocation with warranty tracking.</p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button onClick={activeTab === "services" ? openNew : openNewAsset} className="px-4 py-2 bg-indigo-600 text-white rounded shadow hover:bg-indigo-700">
                + New {activeTab === "services" ? "Service" : "Asset"}
              </button>
            </div>
            <div className="text-xs text-slate-500">Templates for product/service offerings and asset tracking (INR shown).</div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg p-1 shadow-sm border">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("assets")}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === "assets" 
                  ? "bg-teal-600 text-white shadow-sm" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              Asset Management
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === "reports" 
                  ? "bg-emerald-600 text-white shadow-sm" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveTab("activity")}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === "activity" 
                  ? "bg-purple-600 text-white shadow-sm" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              Activity Logs
            </button>
          </div>
        </div>

        <section className="bg-white p-4 rounded-2xl shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <input placeholder={`Search ${activeTab === "services" ? "name or coverage" : activeTab === "assets" ? "asset ID, serial, brand, item, company, or employee" : "assets for reports"}...`} value={query} onChange={e=>setQuery(e.target.value)} className="px-3 py-2 border rounded w-72" />
              {activeTab === "reports" && (
                <div className="flex items-center gap-2">
                  <select value={reportDateRange} onChange={e=>setReportDateRange(e.target.value)} className="px-3 py-2 border rounded text-sm">
                    <option value="all">All Time</option>
                    <option value="daily">Last 24 Hours</option>
                    <option value="weekly">Last Week</option>
                    <option value="monthly">Last Month</option>
                    <option value="quarterly">Last Quarter</option>
                    <option value="half-yearly">Last 6 Months</option>
                    <option value="yearly">Last Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  {reportDateRange === "custom" && (
                    <>
                      <input type="date" value={customStartDate} onChange={e=>setCustomStartDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="Start Date" />
                      <input type="date" value={customEndDate} onChange={e=>setCustomEndDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="End Date" />
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="text-sm text-slate-500">
              Total: <span className="font-medium">{activeTab === "assets" ? assets.length : dateFilteredAssets.length}</span>
              {activeTab === "reports" && (
                <div className="flex gap-2 ml-4">
                  <button onClick={exportToPDF} className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 flex items-center gap-1">
                    <FileText size={14} /> PDF
                  </button>
                  <button onClick={exportToExcel} className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1">
                    <Download size={14} /> Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {activeTab === "services" ? (
              filtered.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No configurations match your filters.</div>
              ) : (
                filtered.map(it => <ItemRow key={it.id} it={it} />)
              )
            ) : activeTab === "assets" ? (
              filteredAssets.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No assets match your search.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial No.</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAssets.map(asset => (
                        <tr key={asset.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <button 
                              onClick={() => openViewAsset(asset)} 
                              className="text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                            >
                              {asset.assetId || 'N/A'}
                            </button>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{asset.serialNumber || 'N/A'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{asset.brandName || 'N/A'}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{asset.itemName}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{asset.category}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{asset.assignedTo || 'Unassigned'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              ['Available', 'Assigned', 'In Use', 'Under Warranty', 'AMC Active'].includes(asset.status) ? 'bg-green-100 text-green-800' :
                              ['Returned', 'Approved', 'Reallocated'].includes(asset.status) ? 'bg-blue-100 text-blue-800' :
                              ['Under Maintenance', 'Requested'].includes(asset.status) ? 'bg-yellow-100 text-yellow-800' :
                              ['Damaged', 'Lost / Stolen', 'Retired', 'Disposed', 'Rejected', 'Warranty Expired', 'AMC Expired'].includes(asset.status) ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {asset.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <button onClick={() => openEditAsset(asset)} className="text-indigo-600 hover:text-indigo-900" title="Edit Asset">
                                <Edit size={16} />
                              </button>
                              {asset.invoicePdfName && (
                                <>
                                  <button 
                                    onClick={() => {
                                      if (asset.invoicePdf && asset.invoicePdf.startsWith('data:')) {
                                        const blob = fetch(asset.invoicePdf).then(res => res.blob());
                                        blob.then(b => {
                                          const url = URL.createObjectURL(b);
                                          window.open(url, '_blank');
                                        });
                                      } else {
                                        alert('PDF not available');
                                      }
                                    }} 
                                    className="text-green-600 hover:text-green-900" 
                                    title="View Invoice PDF"
                                  >
                                    <FileText size={16} />
                                  </button>
                                  <button 
                                    onClick={() => {
                                      if (asset.invoicePdf && asset.invoicePdf.startsWith('data:')) {
                                        const link = document.createElement('a');
                                        link.href = asset.invoicePdf;
                                        link.download = asset.invoicePdfName;
                                        link.click();
                                      } else {
                                        alert('PDF not available');
                                      }
                                    }} 
                                    className="text-blue-600 hover:text-blue-900" 
                                    title="Download Invoice PDF"
                                  >
                                    <Download size={16} />
                                  </button>
                                </>
                              )}
                              <button onClick={() => removeAsset(asset.id)} className="text-red-600 hover:text-red-900" title="Delete Asset">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : activeTab === "activity" ? (
              // Activity Logs Tab
              <div className="space-y-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modified By</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">EMP001</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">Asset Created</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">Admin User</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{new Date().toLocaleDateString()}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">Created new asset AST-001</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">EMP002</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">Status Updated</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">HR Manager</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{new Date().toLocaleDateString()}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">Changed status from Available to Assigned</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              // Reports Tab
              <div className="space-y-6">
                {dateFilteredAssets.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No assets available for reporting in the selected date range.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset ID</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial No.</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchase Date</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocation Date</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warranty Expiry</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deployed To</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dateFilteredAssets.sort((a, b) => {
                          const aNum = parseInt(a.assetId?.replace('AST-', '') || '0');
                          const bNum = parseInt(b.assetId?.replace('AST-', '') || '0');
                          return aNum - bNum;
                        }).map(asset => (
                          <tr key={asset.id} className="hover:bg-gray-50">
                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <button 
                                onClick={() => openViewAsset(asset)} 
                                className="text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                              >
                                {asset.assetId || 'N/A'}
                              </button>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{asset.serialNumber || 'N/A'}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{asset.brandName || 'N/A'}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{asset.category}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{asset.assignedTo || 'Unassigned'}</td>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                ['Available', 'Assigned', 'In Use', 'Under Warranty', 'AMC Active'].includes(asset.status) ? 'bg-green-100 text-green-800' :
                                ['Returned', 'Approved', 'Reallocated'].includes(asset.status) ? 'bg-blue-100 text-blue-800' :
                                ['Under Maintenance', 'Requested'].includes(asset.status) ? 'bg-yellow-100 text-yellow-800' :
                                ['Damaged', 'Lost / Stolen', 'Retired', 'Disposed', 'Rejected', 'Warranty Expired', 'AMC Expired'].includes(asset.status) ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {asset.status}
                              </span>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {asset.allocationDate ? new Date(asset.allocationDate).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              {asset.warrantyExpiryDate ? new Date(asset.warrantyExpiryDate).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{asset.deployedTo || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <Modal open={editorOpen} title={editing?.id ? (editing.name || "Edit Config") : "New Configuration"} onClose={()=>{ setEditorOpen(false); setEditing(null); }}>
        <Editor model={editing} onSave={saveItem} onCancel={()=>{ setEditorOpen(false); setEditing(null); }} />
      </Modal>

      <Modal open={assetEditorOpen} title={editingAsset?.id ? (editingAsset.itemName || "Edit Asset") : "New Asset"} width="max-w-4xl" onClose={()=>{ setAssetEditorOpen(false); setEditingAsset(null); }}>
        <AssetEditor model={editingAsset} onSave={saveAsset} onCancel={()=>{ setAssetEditorOpen(false); setEditingAsset(null); }} />
      </Modal>

      {/* View Asset Details Modal */}
      <Modal open={viewAssetOpen} title={`Asset Details - ${viewingAsset?.assetId || 'N/A'}`} width="max-w-4xl" onClose={()=>{ setViewAssetOpen(false); setViewingAsset(null); }}>
        <AssetViewer asset={viewingAsset} onClose={()=>{ setViewAssetOpen(false); setViewingAsset(null); }} />
      </Modal>

      {/* Add Status Modal */}
      <Modal open={showStatusForm} title="Add New Status" width="max-w-md" onClose={() => { setShowStatusForm(false); setNewStatusName(''); }}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-600 mb-2">Status Name</label>
            <input
              type="text"
              value={newStatusName}
              onChange={(e) => setNewStatusName(e.target.value)}
              placeholder="Enter status name"
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              onKeyPress={(e) => e.key === 'Enter' && addCustomStatus()}
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button
              onClick={() => { setShowStatusForm(false); setNewStatusName(''); }}
              className="px-4 py-2 bg-white border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={addCustomStatus}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Add Status
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Category Modal */}
      <Modal open={showCategoryForm} title="Add New Category" width="max-w-md" onClose={() => { setShowCategoryForm(false); setNewCategoryName(''); }}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-600 mb-2">Category Name</label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name"
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              onKeyPress={(e) => e.key === 'Enter' && addCustomCategory()}
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button
              onClick={() => { setShowCategoryForm(false); setNewCategoryName(''); }}
              className="px-4 py-2 bg-white border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={addCustomCategory}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
            >
              Add Category
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
